"use client";

import { useEffect, useState } from "react";
import { Menu } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-12">
        <div className="h-4 w-16 bg-muted animate-pulse" />
        <div className="h-4 w-20 bg-muted animate-pulse" />
        <div className="h-4 w-24 bg-muted animate-pulse" />
        <div className="h-4 w-16 bg-muted animate-pulse" />
      </div>;
  }
  const items = menuItems.slice(0, Math.max(0, menuItems.length - 1));
  const lastItem = menuItems[menuItems.length - 1];
  return <NavigationMenu viewport={false} className="max-w-none">
      <NavigationMenuList className="gap-12">
        {items.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="text-foreground bg-transparent hover:bg-transparent focus:bg-transparent data-[active=true]:bg-transparent text-[14px] tracking-widest font-default relative px-0 py-1 group">
                <span className="relative">
                  {item.label}
                  <span className="absolute left-0 -bottom-1 h-px w-0 bg-primary transition-all duration-[220ms] ease-out group-hover:w-full" />
                </span>
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
        {lastItem && <NavigationMenuItem data-index={items.length}>
            <NavigationMenuLink asChild>
              <Link to={lastItem.href || "#"} className="text-primary-foreground bg-primary hover:bg-primary/90 focus:bg-primary data-[active=true]:bg-primary border border-primary px-6 py-3 text-[14px] tracking-widest font-default transition-colors duration-300 ease-out rounded-md">
                {lastItem.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onItemClick
}: {
  onItemClick: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-6 mt-12">
        <div className="h-6 w-32 bg-muted animate-pulse" />
        <div className="h-6 w-40 bg-muted animate-pulse" />
        <div className="h-6 w-28 bg-muted animate-pulse" />
      </div>;
  }
  return <nav className="flex flex-col mt-16">
      <div className="font-mono text-[11px] tracking-widest uppercase text-muted-foreground mb-8">
        N° 01 — ARCHIVE
      </div>
      <div className="h-px w-full bg-border mb-8" />
      <ul className="flex flex-col gap-6">
        {menuItems.map((item, i) => <li key={item.id} data-index={i}>
            <Link to={item.href || "#"} onClick={onItemClick} className="text-foreground text-2xl font-default tracking-tight hover:text-primary transition-colors duration-220">
              {item.label}
            </Link>
          </li>)}
      </ul>
    </nav>;
}
export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="1610" id="header" className={`sticky top-0 left-0 right-0 z-50 w-full transition-all duration-[240ms] ease-out bg-background text-foreground ${scrolled ? "bg-background/80 backdrop-blur-md border-b border-border" : "bg-background border-b border-transparent"}`}>
      <div className="w-full max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24 py-6 md:py-7">
        <div className="grid grid-cols-12 items-center gap-4 md:gap-8 relative">
          {}
          <div className="hidden lg:block absolute left-0 -translate-x-full top-1/2 -translate-y-1/2 pr-4">
            
          </div>

          {}
          <div className="col-span-8 md:col-span-4 flex items-center">
            <Link to="/HomePage" className="inline-flex items-center" aria-label="Welcome Baby Ohlson — Home">
              <WvcLogo className="h-7 w-auto text-primary" />
            </Link>
          </div>

          {}
          <div className="hidden md:flex col-span-8 justify-end items-center">
            <MenuProvider menu_id="16">
              <DesktopMenuConsumer />
            </MenuProvider>
          </div>

          {}
          <div className="md:hidden col-span-4 flex justify-end">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" className="font-mono text-[12px] tracking-widest uppercase text-foreground hover:bg-transparent hover:text-primary px-2 h-auto py-1">
                  <Menu />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-background border-l border-border w-full sm:max-w-md px-6 py-8">
                <MenuProvider menu_id="16">
                  <MobileMenuConsumer onItemClick={() => setMobileOpen(false)} />
                </MenuProvider>
                <div className="mt-auto pt-12">
                  <div className="h-px w-full bg-border mb-6" />
                  <span className="font-mono text-[11px] tracking-widest uppercase text-muted-foreground">
                    EST. OHLSON · MMXXIV
                  </span>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </section>;
}